import {
    Component,
    OnInit,
    OnDestroy,
    AfterViewInit,
    ViewChild,
    ChangeDetectorRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTabsModule } from '@angular/material/tabs';
import { MatTableModule, MatTableDataSource } from '@angular/material/table';
import { MatChipsModule } from '@angular/material/chips';
import { MatDividerModule } from '@angular/material/divider';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatListModule } from '@angular/material/list';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatGridListModule } from '@angular/material/grid-list';
import { MatBadgeModule } from '@angular/material/badge';
import { MatRippleModule } from '@angular/material/core';
import { MatPaginatorModule, MatPaginator } from '@angular/material/paginator';
import { Subject, takeUntil, of } from 'rxjs';
import { switchMap, catchError, map } from 'rxjs/operators';

import { LoggerService } from '../../../../core/services/logger.service';
import {
    LiquidacionesService,
    LiquidacionAgencia,
} from '../../../../services/liquidaciones.service';
import type {
    LiquidacionAgenciaContenedoresTabResponse,
    LiquidacionAgenciaExtraServiciosTabResponse,
    LiquidacionAgenciaTotalesTabResponse,
} from '../../../../services/liquidaciones.service';
import { LiquidacionesNavigationService } from '../../../../services/liquidaciones-navigation.service';
import { ParameterService } from '../../../../services/parameter.service';

@Component({
    selector: 'app-liquidacion-agencia-detalle',
    standalone: true,
    imports: [
        CommonModule,
        MatCardModule,
        MatButtonModule,
        MatIconModule,
        MatTabsModule,
        MatTableModule,
        MatChipsModule,
        MatDividerModule,
        MatProgressSpinnerModule,
        MatSnackBarModule,
        MatTooltipModule,
        MatListModule,
        MatExpansionModule,
        MatGridListModule,
        MatBadgeModule,
        MatRippleModule,
        MatPaginatorModule,
    ],
    providers: [LiquidacionesService, LoggerService, ParameterService],
    templateUrl: './liquidacion-agencia-detalle.component.html',
    styleUrl: './liquidacion-agencia-detalle.component.scss',
})
export class LiquidacionAgenciaDetalleComponent
    implements OnInit, AfterViewInit, OnDestroy
{
    private destroy$ = new Subject<void>();

    /** Versión del endpoint Excel a usar (v1 o v2), obtenida del parámetro LA_VERSION_EXCEL */
    private excelVersion: 'v1' | 'v2' = 'v1';

    liquidacionId: number | null = null;
    liquidacion: LiquidacionAgencia | null = null;
    isLoading = true;
    error: string | null = null;

    // Datos para tabs
    selectedTabIndex = 0; // Resumen Financiero será el primer tab

    // Datos del resumen financiero
    resumenFinanciero: any = null;

    // Datos consolidados del Excel (para mostrar en la sección de totales consolidados)
    datosConsolidados: any = null;

    // Totales por nave (sección "Totales" del tab)
    totalesPorNaveRows: any[] = [];
    totalesPorNaveGlobalRow: any | null = null;
    totalesPorNaveDataSource = new MatTableDataSource<any>([]);
    totalesPorNaveColumns: string[] = [
        'nave',
        'pol',
        'etd',
        'exportador',
        'cntrs',
        'sConv',
        'plts',
        'cajas',
        'montoAge',
        'montoTransporte',
        'montoOrdenesServicios',
        'gestionOpbNeto',
        'totalVentaNeto',
        'totalTraspasoBruto',
    ];

    /** Tab Extra Servicios — dbo.sp_DetalleLiqAgenciaExtraServicios */
    extraServiciosColumns: string[] = [];
    extraServicios: Record<string, string | number | null>[] = [];
    extraServiciosDataSource = new MatTableDataSource<
        Record<string, string | number | null>
    >([]);
    extraServiciosTotalsRow: Record<string, string | number | null> | null =
        null;

    private readonly extraServiciosColumnasTexto = new Set([
        'Exportador',
        'NroOS',
        'RefCliente',
        'Servicio',
        'TipoFacturacion',
    ]);

    // Datos de contenedores (columnas dinámicas desde SP)
    contenedores: Record<string, string | number | null>[] = [];
    totalesContenedoresRow: Record<string, string | number | null> | null =
        null;
    contenedoresDataSource = new MatTableDataSource<
        Record<string, string | number | null>
    >([]);
    contenedoresColumns: string[] = [];

    private readonly contenedorColumnasTexto = new Set([
        'Nave',
        'POL',
        'Exportador',
        'NroContenedor',
        'RefCliente',
    ]);

    // Datos de pallets (columnas dinámicas desde SP)
    pallets: Record<string, string | number | null>[] = [];
    totalesPalletsRow: Record<string, string | number | null> | null = null;
    palletsDataSource = new MatTableDataSource<
        Record<string, string | number | null>
    >([]);
    palletsColumns: string[] = [];

    private readonly palletColumnasTexto = new Set([
        'Nave',
        'POL',
        'Exportador',
        'RefCliente',
    ]);

    // Estados de carga por tab
    isLoadingTotales = false;
    isLoadingContenedores = false;
    isLoadingPallets = false;
    isLoadingExtraServicios = false;

    /**
     * Carga bajo demanda de las pestañas: los datos de cada una se piden recién
     * cuando se abre por primera vez. `datosBaseListos` se marca cuando responde
     * (o falla) pdf-data; antes de eso no se carga ninguna pestaña.
     */
    private datosBaseListos = false;
    /** Pestañas ya pedidas (cargadas o en curso): 0 Totales, 1 Contenedores, 2 Pallets, 3 Extra Servicios. */
    private pestanasSolicitadas = new Set<number>();

    constructor(
        // eslint-disable-next-line no-unused-vars
        private route: ActivatedRoute,
        // eslint-disable-next-line no-unused-vars
        private router: Router,
        // eslint-disable-next-line no-unused-vars
        private liquidacionesService: LiquidacionesService,
        // eslint-disable-next-line no-unused-vars
        private logger: LoggerService,
        // eslint-disable-next-line no-unused-vars
        private navigationService: LiquidacionesNavigationService,
        // eslint-disable-next-line no-unused-vars
        private cdr: ChangeDetectorRef,
        // eslint-disable-next-line no-unused-vars
        private parameterService: ParameterService
    ) {}

    ngOnInit(): void {
        // Primero obtener la versión del Excel configurada, luego cargar los datos
        this.parameterService
            .getLiquidacionAgenciaExcelVersion()
            .pipe(
                takeUntil(this.destroy$),
                switchMap((version) => {
                    this.excelVersion = version;
                    this.logger.info(
                        `Versión Excel configurada: ${version}`,
                        'LiquidacionAgenciaDetalleComponent'
                    );
                    return this.route.params;
                })
            )
            .subscribe((params) => {
                this.liquidacionId = Number(params['id']);
                if (this.liquidacionId) {
                    this.cargarDetalleLiquidacion();
                } else {
                    this.error = 'ID de liquidación no válido';
                    this.isLoading = false;
                }
            });
    }

    @ViewChild('contenedoresPaginator') contenedoresPaginator!: MatPaginator;
    @ViewChild('palletsPaginator') palletsPaginator!: MatPaginator;
    @ViewChild('extraServiciosPaginator')
    extraServiciosPaginator!: MatPaginator;

    ngAfterViewInit(): void {
        this.conectarContenedoresTabla();
        this.conectarExtraServiciosTabla();
    }

    onTabIndexChange(index: number): void {
        // Carga la pestaña la primera vez que se abre (una vez que pdf-data respondió)
        if (this.datosBaseListos) {
            this.cargarPestana(index);
        }
        // Re-conectar paginator/sort al cambiar de tab (por *ngIf del template)
        if (index === 3) {
            this.conectarExtraServiciosTabla();
            this.extraServiciosDataSource._updateChangeSubscription();
            this.cdr.detectChanges();
        } else if (index === 1) {
            this.conectarContenedoresTabla();
            this.cdr.detectChanges();
        } else if (index === 2) {
            this.conectarPalletsTabla();
            this.cdr.detectChanges();
        }
    }

    private conectarContenedoresTabla(): void {
        // Ejecutar en el siguiente ciclo para asegurar que las referencias existen
        setTimeout(() => {
            if (this.contenedoresPaginator) {
                this.contenedoresDataSource.paginator =
                    this.contenedoresPaginator;
                // Escuchar cambios de paginación para refrescar correctamente
                this.contenedoresPaginator.page.subscribe(() => {
                    // Forzar actualización de la vista
                    this.cdr.detectChanges();
                });
            }
            this.cdr.detectChanges();
        });
    }

    private conectarExtraServiciosTabla(): void {
        setTimeout(() => {
            if (this.extraServiciosPaginator) {
                this.extraServiciosDataSource.paginator =
                    this.extraServiciosPaginator;
                this.extraServiciosPaginator.page.subscribe(() => {
                    this.cdr.detectChanges();
                });
            }
            this.cdr.detectChanges();
        });
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    private cargarDetalleLiquidacion(): void {
        if (!this.liquidacionId) return;

        this.isLoading = true;
        this.error = null;
        this.datosBaseListos = false;
        this.pestanasSolicitadas.clear();
        // El componente se reutiliza al cambiar el id de la ruta: sin esto, una pestaña aún
        // no abierta podría mostrar datos de la liquidación anterior.
        this.limpiarDatosPestanas();

        this.liquidacionesService
            .getLiquidacionAgenciaById(this.liquidacionId)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (liquidacion) => {
                    // Asegurar que el estado tenga un valor por defecto si no viene del backend
                    if (
                        !liquidacion.estado ||
                        liquidacion.estado.trim() === ''
                    ) {
                        liquidacion.estado = 'Pendiente';
                    }
                    this.liquidacion = liquidacion;
                    this.cargarDatosAdicionales();
                },
                error: (error) => {
                    this.logger.error(
                        `Error al cargar detalle de liquidación ${this.liquidacionId}`,
                        error,
                        'LiquidacionAgenciaDetalleComponent'
                    );
                    this.error = 'Error al cargar los datos de la liquidación';
                    this.isLoading = false;
                },
            });
    }

    /** Vacía los datos de las cuatro pestañas (al cargar otra liquidación). */
    private limpiarDatosPestanas(): void {
        this.datosConsolidados = null;
        this.totalesPorNaveRows = [];
        this.totalesPorNaveGlobalRow = null;
        this.totalesPorNaveDataSource.data = [];
        this.contenedores = [];
        this.contenedoresColumns = [];
        this.totalesContenedoresRow = null;
        this.contenedoresDataSource.data = [];
        this.pallets = [];
        this.palletsColumns = [];
        this.totalesPalletsRow = null;
        this.palletsDataSource.data = [];
        this.extraServicios = [];
        this.extraServiciosColumns = [];
        this.extraServiciosTotalsRow = null;
        this.extraServiciosDataSource.data = [];
    }

    /** Pide los datos de una pestaña solo si todavía no se pidieron. */
    private cargarPestana(index: number): void {
        if (this.pestanasSolicitadas.has(index)) return;
        this.pestanasSolicitadas.add(index);
        switch (index) {
            case 0:
                this.cargarDatosConsolidados();
                break;
            case 1:
                this.cargarDatosContenedores();
                break;
            case 2:
                this.cargarDatosPallets();
                break;
            case 3:
                this.cargarDatosExtraServicios();
                break;
        }
    }

    /** Marca los datos base como listos y carga solo la pestaña activa en este momento. */
    private cargarPestanaActivaTrasDatosBase(): void {
        this.datosBaseListos = true;
        const activa = this.selectedTabIndex;
        if (activa !== 0) {
            // El spinner global solo espera a Totales; si no es la pestaña activa, no se bloquea la pantalla.
            this.isLoading = false;
        }
        this.cargarPestana(activa);
    }

    private cargarDatosAdicionales(): void {
        if (!this.liquidacionId) return;

        // Cargar datos del PDF (que incluyen información detallada)
        this.liquidacionesService
            .getLiquidacionAgenciaPdfData(this.liquidacionId)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (pdfData) => {
                    const resumenDesdePdf =
                        this.mapResumenFinancieroDesdePdf(pdfData);
                    if (resumenDesdePdf) {
                        this.resumenFinanciero = resumenDesdePdf;
                        this.logger.info(
                            'Resumen financiero cargado desde pdf-data',
                            'LiquidacionAgenciaDetalleComponent'
                        );
                    } else {
                        this.resumenFinanciero = this.generarResumenBasico();
                        this.logger.warn(
                            'No se pudo mapear resumen financiero desde pdf-data, usando resumen básico',
                            'LiquidacionAgenciaDetalleComponent'
                        );
                    }

                    // Solo se carga la pestaña activa; las demás se piden al abrirlas
                    this.cargarPestanaActivaTrasDatosBase();
                },
                error: (error) => {
                    this.logger.error(
                        `Error al cargar datos adicionales de liquidación ${this.liquidacionId}`,
                        error,
                        'LiquidacionAgenciaDetalleComponent'
                    );
                    // Si falla, usar datos básicos
                    this.resumenFinanciero = this.generarResumenBasico();
                    this.extraServiciosColumns = [];
                    this.extraServiciosTotalsRow = null;
                    this.extraServicios = [];
                    this.extraServiciosDataSource.data = [];
                    this.contenedores = [];
                    this.contenedoresColumns = [];
                    this.totalesContenedoresRow = null;
                    this.contenedoresDataSource.data = [];
                    this.pallets = [];
                    this.palletsColumns = [];
                    this.totalesPalletsRow = null;
                    this.palletsDataSource.data = [];
                    // Datos base listos: se carga solo la pestaña activa (Totales apaga isLoading al terminar)
                    this.cargarPestanaActivaTrasDatosBase();
                },
            });
    }

    private cargarDatosContenedores(): void {
        if (!this.liquidacionId) return;

        this.isLoadingContenedores = true;

        this.liquidacionesService
            .getLiquidacionAgenciaContenedoresTab(this.liquidacionId)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (response) => {
                    const data = (
                        response as {
                            data?: LiquidacionAgenciaContenedoresTabResponse;
                        }
                    )?.data;
                    if (data && Array.isArray(data.columns)) {
                        this.contenedoresColumns = [...data.columns];
                        this.contenedores = data.rows ?? [];
                        this.totalesContenedoresRow = data.totalsRow ?? null;
                        this.contenedoresDataSource.data = [
                            ...this.contenedores,
                        ];
                        this.logger.info(
                            `Contenedores(tab) cargados: ${this.contenedores.length} cols=${this.contenedoresColumns.length}`,
                            'LiquidacionAgenciaDetalleComponent'
                        );
                        this.conectarContenedoresTabla();
                    } else {
                        this.contenedoresColumns = [];
                        this.totalesContenedoresRow = null;
                        this.contenedores = [];
                        this.contenedoresDataSource.data = [];
                        this.conectarContenedoresTabla();
                    }
                    this.isLoadingContenedores = false;
                },
                error: (error) => {
                    this.logger.error(
                        `Error al cargar contenedores: ${error}`,
                        error,
                        'LiquidacionAgenciaDetalleComponent'
                    );
                    this.contenedoresColumns = [];
                    this.totalesContenedoresRow = null;
                    this.contenedores = [];
                    this.contenedoresDataSource.data = [];
                    this.conectarContenedoresTabla();
                    this.isLoadingContenedores = false;
                },
            });
    }

    /**
     * Compatibilidad: el template histórico del tab contenedores
     * usa `servicios` dinámicos para varias columnas.
     */
    getServicioValue(contenedor: any, servicioKey: string): number {
        return contenedor?.servicios?.[servicioKey] || 0;
    }

    esColumnaTextoContenedor(col: string): boolean {
        return this.contenedorColumnasTexto.has(col);
    }

    formatoCeldaContenedorTab(
        col: string,
        value: string | number | null | undefined
    ): string {
        if (value === null || value === undefined || value === '') {
            return '';
        }
        if (col === 'PLTS') {
            const n = typeof value === 'number' ? value : Number(value);
            return Number.isFinite(n) ? this.formatearNumero(n) : String(value);
        }
        if (col === 'ETD') {
            return this.formatearFechaCorta(
                typeof value === 'string' ? value : String(value)
            );
        }
        if (this.esColumnaTextoContenedor(col)) {
            return String(value);
        }
        const n = typeof value === 'number' ? value : Number(value);
        if (Number.isFinite(n)) {
            return this.formatearMoneda(n);
        }
        return String(value);
    }

    private cargarDatosConsolidados(): void {
        if (!this.liquidacionId) return;
        const id = this.liquidacionId;
        this.isLoadingTotales = true;

        // Primero el procedimiento dedicado (totales-tab, sp_DetalleLiqAgenciaTotales).
        // El Excel V1 (más costoso) solo se pide como respaldo si el SP falla o viene vacío.
        this.liquidacionesService
            .getLiquidacionAgenciaTotalesTab(id)
            .pipe(
                takeUntil(this.destroy$),
                catchError((err) => {
                    this.logger.warn(
                        `Totales-tab (SP) no disponible; usando excel-data. ${String(
                            err
                        )}`,
                        'LiquidacionAgenciaDetalleComponent'
                    );
                    return of<{
                        success: boolean;
                        data?: LiquidacionAgenciaTotalesTabResponse;
                    }>({ success: false });
                }),
                switchMap((spResponse) => {
                    const payload = spResponse?.data;
                    const tieneSp =
                        payload &&
                        ((payload.rows?.length ?? 0) > 0 ||
                            payload.totalsRow != null);
                    if (tieneSp) {
                        this.aplicarTotalesDesdeSp(payload);
                        this.logger.info(
                            'Tab Totales cargado desde SP (totales-tab)',
                            'LiquidacionAgenciaDetalleComponent'
                        );
                        return of(null);
                    }
                    return this.liquidacionesService
                        .getLiquidacionAgenciaExcelDataByVersion(id, 'v1')
                        .pipe(
                            map((response) => ({ response })),
                            catchError((err) => {
                                this.logger.error(
                                    `Error al cargar datos consolidados (respaldo Excel V1): ${err}`,
                                    err,
                                    'LiquidacionAgenciaDetalleComponent'
                                );
                                return of({ response: null });
                            })
                        );
                })
            )
            .subscribe({
                next: (respaldo) => {
                    if (respaldo !== null) {
                        const totales = respaldo.response?.data?.totales;
                        if (totales) {
                            this.datosConsolidados = totales;
                            this.procesarTotalesPorNaveDesdeExcel(totales);
                        } else {
                            this.datosConsolidados = null;
                            this.logger.warn(
                                'No se encontraron datos consolidados en Excel V1',
                                'LiquidacionAgenciaDetalleComponent'
                            );
                            this.totalesPorNaveRows = [];
                            this.totalesPorNaveGlobalRow = null;
                            this.totalesPorNaveDataSource.data = [];
                        }
                    }
                    this.isLoading = false;
                    this.isLoadingTotales = false;
                },
                error: (error) => {
                    this.logger.error(
                        `Error al cargar datos consolidados: ${error}`,
                        error,
                        'LiquidacionAgenciaDetalleComponent'
                    );
                    this.isLoading = false;
                    this.isLoadingTotales = false;
                },
            });
    }

    /**
     * Filas del tab Totales alineadas a dbo.sp_DetalleLiqAgenciaTotales (backend totales-tab).
     */
    private aplicarTotalesDesdeSp(
        data: LiquidacionAgenciaTotalesTabResponse
    ): void {
        const mapped = (data.rows ?? []).map((r) =>
            this.mapTotalesSpFilaADetalle(r)
        );
        const extraRows = mapped.filter((r) =>
            this.esFilaExtraServiciosTotales(r)
        );
        const normalRows = mapped.filter(
            (r) => !this.esFilaExtraServiciosTotales(r)
        );
        const detailRows = [...normalRows, ...extraRows];
        let totalRow: Record<string, unknown> | null = null;
        if (data.totalsRow) {
            totalRow = this.mapTotalesSpFilaADetalle(data.totalsRow);
            totalRow['nave'] = 'TOTALES';
        }
        const allRows = totalRow ? [...detailRows, totalRow] : [...detailRows];
        this.totalesPorNaveGlobalRow = totalRow;
        this.totalesPorNaveRows = allRows;
        this.totalesPorNaveDataSource.data = [...allRows];
    }

    private mapTotalesSpFilaADetalle(
        r: Record<string, string | number | null>
    ): Record<string, unknown> {
        const g = (a: string, b: string): string | number | null =>
            (r[a] ?? r[b]) as string | number | null;
        const num = (v: unknown): number => {
            if (v === null || v === undefined || v === '') return 0;
            const n = typeof v === 'number' ? v : Number(v);
            return Number.isFinite(n) ? n : 0;
        };
        const nl = String(
            g('NumeroLiquidacion', 'numeroLiquidacion') ?? ''
        ).trim();
        const naveRaw = g('Nave', 'nave');
        const nave =
            nl === 'Totales'
                ? 'TOTALES'
                : naveRaw === null || naveRaw === undefined
                ? ''
                : String(naveRaw);
        return {
            numeroLiquidacion: nl || null,
            nave,
            pol: g('POL', 'pol'),
            etd: g('ETD', 'etd'),
            exportador: g('Exportador', 'exportador'),
            cntrs: num(g('CNTR', 'cntrs')),
            sConv: num(g('SConv', 'sConv')),
            plts: num(g('PLTS', 'plts')),
            cajas: num(g('Cajas', 'cajas')),
            montoAge: num(g('MontoAge', 'montoAge')),
            montoTransporte: num(g('MontoTransporte', 'montoTransporte')),
            montoOrdenesServicios: num(
                g('MontoOrdenesServicios', 'montoOrdenesServicios')
            ),
            gestionOpbNeto: num(g('GestionOpbNeto', 'gestionOpbNeto')),
            totalVentaNeto: num(g('TotalVentaNeto', 'totalVentaNeto')),
            totalTraspasoBruto: num(
                g('TotalTraspasoBruto', 'totalTraspasoBruto')
            ),
        };
    }

    /** Fallback: filas desde excel-data V1 (fila Totales del builder puede diferir del SP). */
    private procesarTotalesPorNaveDesdeExcel(totales: any): void {
        const rowsRaw: any[] = (totales?.rows ?? []) as any[];
        const globalRow = rowsRaw.find(
            (r) => r?.numeroLiquidacion === 'Totales'
        );
        const rest = rowsRaw.filter((r) => r?.numeroLiquidacion !== 'Totales');
        const extraRows = rest.filter((r) =>
            this.esFilaExtraServiciosTotales(r)
        );
        const normalRows = rest.filter(
            (r) => !this.esFilaExtraServiciosTotales(r)
        );
        const rows = globalRow
            ? [...normalRows, ...extraRows, globalRow]
            : [...normalRows, ...extraRows];
        this.totalesPorNaveGlobalRow = globalRow ?? null;
        this.totalesPorNaveRows = rows;
        this.totalesPorNaveDataSource.data = [...rows];
    }

    /** Fila agregada Extra Servicios (legacy): visible en tab Totales, ordenada antes de Total. */
    private esFilaExtraServiciosTotales(row: any): boolean {
        const n = (row?.nave ?? '').toString().trim().toUpperCase();
        return n === 'EXTRA SERVICIOS';
    }

    private cargarDatosPallets(): void {
        if (!this.liquidacionId) return;

        this.isLoadingPallets = true;

        this.liquidacionesService
            .getLiquidacionAgenciaPalletsTab(this.liquidacionId)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (response) => {
                    const data = (
                        response as {
                            data?: LiquidacionAgenciaContenedoresTabResponse;
                        }
                    )?.data;
                    if (data && Array.isArray(data.columns)) {
                        this.palletsColumns = [...data.columns];
                        this.pallets = data.rows ?? [];
                        this.totalesPalletsRow = data.totalsRow ?? null;
                        this.palletsDataSource.data = [...this.pallets];
                        this.logger.info(
                            `Pallets(tab) cargados: ${this.pallets.length} cols=${this.palletsColumns.length}`,
                            'LiquidacionAgenciaDetalleComponent'
                        );
                        this.conectarPalletsTabla();
                    } else {
                        this.palletsColumns = [];
                        this.totalesPalletsRow = null;
                        this.pallets = [];
                        this.palletsDataSource.data = [];
                        this.conectarPalletsTabla();
                    }
                    this.isLoadingPallets = false;
                },
                error: (error) => {
                    this.logger.error(
                        `Error al cargar pallets: ${error}`,
                        error,
                        'LiquidacionAgenciaDetalleComponent'
                    );
                    this.palletsColumns = [];
                    this.totalesPalletsRow = null;
                    this.pallets = [];
                    this.palletsDataSource.data = [];
                    this.conectarPalletsTabla();
                    this.isLoadingPallets = false;
                },
            });
    }

    private cargarDatosExtraServicios(): void {
        if (!this.liquidacionId) return;

        this.isLoadingExtraServicios = true;

        this.liquidacionesService
            .getLiquidacionAgenciaExtraServiciosTab(this.liquidacionId)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (response) => {
                    const data = (
                        response as {
                            data?: LiquidacionAgenciaExtraServiciosTabResponse;
                        }
                    )?.data;
                    if (data && Array.isArray(data.columns)) {
                        this.extraServiciosColumns = [...data.columns];
                        this.extraServicios = data.rows ?? [];
                        this.extraServiciosTotalsRow = data.totalsRow ?? null;
                        this.extraServiciosDataSource.data = [
                            ...this.extraServicios,
                        ];
                        this.logger.info(
                            `Extra servicios (tab): ${this.extraServicios.length} filas`,
                            'LiquidacionAgenciaDetalleComponent'
                        );
                        this.conectarExtraServiciosTabla();
                    } else {
                        this.extraServiciosColumns = [];
                        this.extraServiciosTotalsRow = null;
                        this.extraServicios = [];
                        this.extraServiciosDataSource.data = [];
                        this.conectarExtraServiciosTabla();
                    }
                    this.isLoadingExtraServicios = false;
                },
                error: (error) => {
                    this.logger.error(
                        `Error al cargar extra servicios (tab): ${error}`,
                        error,
                        'LiquidacionAgenciaDetalleComponent'
                    );
                    this.extraServiciosColumns = [];
                    this.extraServiciosTotalsRow = null;
                    this.extraServicios = [];
                    this.extraServiciosDataSource.data = [];
                    this.conectarExtraServiciosTabla();
                    this.isLoadingExtraServicios = false;
                },
            });
    }

    esColumnaTextoExtraServicio(col: string): boolean {
        return this.extraServiciosColumnasTexto.has(col);
    }

    formatoCeldaExtraServiciosTab(
        col: string,
        value: string | number | null | undefined
    ): string {
        if (value === null || value === undefined || value === '') {
            return '';
        }
        if (col === 'Cantidad') {
            const n = typeof value === 'number' ? value : Number(value);
            return Number.isFinite(n)
                ? new Intl.NumberFormat('es-CL', {
                      minimumFractionDigits: 0,
                      maximumFractionDigits: 2,
                  }).format(n)
                : String(value);
        }
        if (this.esColumnaTextoExtraServicio(col)) {
            return String(value);
        }
        const n = typeof value === 'number' ? value : Number(value);
        if (Number.isFinite(n)) {
            return this.formatearMoneda(n);
        }
        return String(value);
    }

    private conectarPalletsTabla(): void {
        setTimeout(() => {
            if (this.palletsPaginator) {
                this.palletsDataSource.paginator = this.palletsPaginator;
                this.palletsPaginator.page.subscribe(() => {
                    this.cdr.detectChanges();
                });
            }
            this.cdr.detectChanges();
        });
    }

    esColumnaTextoPallet(col: string): boolean {
        return this.palletColumnasTexto.has(col);
    }

    formatoCeldaPalletTab(
        col: string,
        value: string | number | null | undefined
    ): string {
        if (value === null || value === undefined || value === '') {
            return '';
        }
        if (col === 'PLTS') {
            const n = typeof value === 'number' ? value : Number(value);
            return Number.isFinite(n) ? this.formatearNumero(n) : String(value);
        }
        if (col === 'ETD') {
            return this.formatearFechaCorta(
                typeof value === 'string' ? value : String(value)
            );
        }
        if (this.esColumnaTextoPallet(col)) {
            return String(value);
        }
        const n = typeof value === 'number' ? value : Number(value);
        if (Number.isFinite(n)) {
            return this.formatearMoneda(n);
        }
        return String(value);
    }

    private mapResumenFinancieroDesdePdf(pdfData: any): any | null {
        const data = pdfData?.data ?? {};
        const resumenFinanciero = data?.resumenFinanciero;
        const resumenOperacion = data?.resumenOperacion; // ✅ Usar resumenOperacion para CNTR/PLTS

        if (!resumenFinanciero && !resumenOperacion) {
            return null;
        }

        // ✅ Valores desde resumenFinanciero (para compatibilidad)
        const totalAgenciamiento = this.parseClp(
            resumenFinanciero?.totalAgenciamiento
        );
        const totalDocumentacion = this.parseClp(
            resumenFinanciero?.totalDocumentacion
        );
        const totalCoordinacion = this.parseClp(
            resumenFinanciero?.totalCoordinacion
        );
        const totalServiciosAdicionales = this.parseClp(
            resumenFinanciero?.totalServiciosAdicionales
        );
        const subtotal = this.parseClp(resumenFinanciero?.subtotal);
        const comisionTotal = this.parseClp(resumenFinanciero?.comisionTotal);
        const descuentos = this.parseClp(resumenFinanciero?.descuentos);
        const baseImponible = this.parseClp(resumenFinanciero?.baseImponible);

        // ✅ CORREGIDO: Usar resumenOperacion para valores del resumen de operación
        const iva = this.parseClp(
            resumenOperacion?.iva ?? resumenFinanciero?.iva
        );
        const totalBruto = this.parseClp(
            resumenOperacion?.totalBruto ?? resumenFinanciero?.totalBruto
        );
        const totalPagar = this.parseClp(
            resumenOperacion?.totalPagar ??
                resumenFinanciero?.totalAPagar ??
                resumenFinanciero?.totalBruto
        );

        // ✅ CORREGIDO: Usar resumenOperacion para valores CNTR y PLTS (igual que en el PDF)
        const agenciamientoCntr = this.parseClp(
            resumenOperacion?.agenciamientoCntr ?? 0
        );
        const agenciamientoPlts = this.parseClp(
            resumenOperacion?.agenciamientoPlts ?? 0
        );
        const agenciamientoSubtotal = this.parseClp(
            resumenOperacion?.agenciamiento ??
                agenciamientoCntr + agenciamientoPlts
        );

        const transporteCntr = this.parseClp(
            resumenOperacion?.transporteCntr ?? 0
        );
        const transportePlts = this.parseClp(
            resumenOperacion?.transportePlts ?? 0
        );
        const transporteSubtotal = this.parseClp(
            resumenOperacion?.transporte ?? transporteCntr + transportePlts
        );

        const osVentaCntr = this.parseClp(resumenOperacion?.osVentaCntr ?? 0);
        const osVentaPlts = this.parseClp(resumenOperacion?.osVentaPlts ?? 0);
        const osVentaSubtotal = this.parseClp(
            resumenOperacion?.osVenta ?? osVentaCntr + osVentaPlts
        );

        const osTraspasoCntr = this.parseClp(
            resumenOperacion?.osTraspassoCntr ?? 0
        );
        const osTraspasoPlts = this.parseClp(
            resumenOperacion?.osTraspassoPlts ?? 0
        );
        const osTraspasoSubtotal = this.parseClp(
            resumenOperacion?.osTraspaso ?? osTraspasoCntr + osTraspasoPlts
        );

        const totalNetoCntr = this.parseClp(
            resumenOperacion?.totalNetoCntr ?? 0
        );
        const totalNetoPlts = this.parseClp(
            resumenOperacion?.totalNetoPlts ?? 0
        );
        const totalNetoSubtotal = this.parseClp(
            resumenOperacion?.totalNeto ?? totalNetoCntr + totalNetoPlts
        );

        return {
            totalAgenciamiento,
            totalDocumentacion,
            totalCoordinacion,
            totalServiciosAdicionales,
            subtotal,
            comisionTotal,
            descuentos,
            baseImponible,
            totalNeto: totalNetoSubtotal, // ✅ Usar totalNeto desde resumenOperacion
            iva,
            totalBruto,
            osVenta: osVentaSubtotal, // ✅ Usar desde resumenOperacion
            osTraspaso: osTraspasoSubtotal, // ✅ Usar desde resumenOperacion
            totalPagar,
            cntr: resumenFinanciero?.cntr ?? this.liquidacion?.cntr ?? 0,
            plts: resumenFinanciero?.plts ?? this.liquidacion?.plts ?? 0,
            totalFacturado: this.parseClp(
                resumenFinanciero?.totalFacturado ?? totalPagar
            ),
            // ✅ Montos desglosados por tipo de embarque (CNTR vs PLTS) desde resumenOperacion
            agenciamientoCntr,
            agenciamientoPlts,
            agenciamientoSubtotal,
            transporteCntr,
            transportePlts,
            transporteSubtotal,
            osVentaCntr,
            osVentaPlts,
            osVentaSubtotal,
            osTraspasoCntr,
            osTraspasoPlts,
            osTraspasoSubtotal,
            totalNetoCntr,
            totalNetoPlts,
            totalNetoSubtotal,
        };
    }

    private parseClp(value: unknown): number {
        if (value === null || value === undefined) {
            return 0;
        }
        if (typeof value === 'number' && Number.isFinite(value)) {
            return value;
        }
        const normalized = String(value).replace(/[^0-9-]/g, '');
        const parsed = Number(normalized);
        return Number.isFinite(parsed) ? parsed : 0;
    }

    // Fila global: SP (Totales + Nave NULL → mapeamos a TOTALES) o Excel legacy
    isGlobalTotalesRow(row: any): boolean {
        if (!row) return false;
        if (row.nave === 'TOTALES') return true;
        const nl = String(row.numeroLiquidacion ?? '').trim();
        return nl === 'Totales';
    }

    private generarResumenBasico(): any {
        if (!this.liquidacion) {
            this.logger.warn(
                'No hay liquidación disponible para generar resumen básico',
                'LiquidacionAgenciaDetalleComponent'
            );
            return null;
        }

        const agenciamiento = this.liquidacion.agenciamiento || 0;
        const transporte = this.liquidacion.transporte || 0;
        const osVenta = this.liquidacion.osVenta || 0;
        const osTraspaso = this.liquidacion.osTraspaso || 0;
        const total = this.liquidacion.total || 0;

        const totalServiciosAdicionales = transporte + osVenta + osTraspaso;
        const subtotal = agenciamiento + totalServiciosAdicionales;
        const descuentos = 0;
        const baseImponible = subtotal - descuentos;
        const totalNeto = baseImponible; // Total neto es sin IVA
        const iva = Math.round(totalNeto * 0.19);
        const totalBruto = totalNeto + iva; // Total bruto incluye IVA

        const resumen = {
            totalAgenciamiento: agenciamiento,
            totalServiciosAdicionales: totalServiciosAdicionales,
            subtotal: subtotal,
            comisionTotal: total,
            descuentos: descuentos,
            baseImponible: baseImponible,
            iva: iva,
            totalNeto: totalNeto,
            totalBruto: totalBruto,
            totalPagar: totalBruto,
            cntr: this.liquidacion.cntr || 0,
            plts: this.liquidacion.plts || 0,
            totalFacturado: totalBruto, // Usamos totalBruto como total a pagar
        };

        this.logger.info(
            `Resumen básico generado: Agenciamiento=${agenciamiento}, Total=${total}`,
            'LiquidacionAgenciaDetalleComponent'
        );

        return resumen;
    }

    private generarDatosContenedores(): any[] {
        if (!this.liquidacion || this.liquidacion.cntr === 0) return [];

        const contenedores = [];
        for (let i = 1; i <= this.liquidacion.cntr; i++) {
            contenedores.push({
                numero: `CNTR-${String(i).padStart(3, '0')}`,
                tipo: '40HC',
                estado: 'Cargado',
                peso: Math.floor(Math.random() * 20000) + 10000,
                volumen: Math.floor(Math.random() * 60) + 20,
                puerto: 'San Antonio',
                naviera: 'MSC',
                fechaCarga: this.liquidacion.fechaLiquidacion,
            });
        }
        return contenedores;
    }

    // Métodos de cálculo para totales
    calcularTotalServicios(): number {
        return this.extraServicios.reduce((total, row) => {
            const v =
                Number(row['TotalVentaNeto'] ?? 0) +
                Number(row['TotalTraspasoBruto'] ?? 0);
            return total + (Number.isFinite(v) ? v : 0);
        }, 0);
    }

    calcularPesoTotalPallets(): number {
        return this.pallets.reduce((total, pallet) => {
            const raw = pallet?.['pesoKg'];
            const peso = Number(raw);
            return total + (Number.isFinite(peso) ? peso : 0);
        }, 0);
    }

    // Métodos de utilidad
    formatearMoneda(valor: number | undefined | null): string {
        if (valor === undefined || valor === null || isNaN(valor)) {
            return '$ 0';
        }
        const formatted = new Intl.NumberFormat('es-CL', {
            style: 'decimal',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        }).format(valor);
        return `$ ${formatted}`;
    }

    formatearMontoCeroVacio(valor: number | undefined | null): string {
        if (
            valor === undefined ||
            valor === null ||
            isNaN(valor) ||
            valor === 0
        ) {
            return ''; // Mostrar vacío si es 0, null, undefined o NaN
        }
        const formatted = new Intl.NumberFormat('es-CL', {
            style: 'decimal',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        }).format(valor);
        return `$ ${formatted}`;
    }

    formatearNumero(valor: number | undefined | null): string {
        if (valor === undefined || valor === null || isNaN(valor)) {
            return '0';
        }
        return new Intl.NumberFormat('es-CL').format(valor);
    }

    formatearFecha(fecha: string | Date | undefined | null): string {
        if (!fecha) {
            return 'N/A';
        }
        try {
            return new Date(fecha).toLocaleDateString('es-CL', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
            });
        } catch (error) {
            return 'Fecha inválida';
        }
    }

    formatearFechaCorta(fecha: string | Date | undefined | null): string {
        if (!fecha) {
            return 'N/A';
        }
        try {
            // Si la fecha viene como "YYYY-MM-DD" (string), JS la interpreta como UTC,
            // lo que puede desplazar el día según la zona horaria. Para evitarlo,
            // parseamos manualmente.
            if (typeof fecha === 'string') {
                const m = fecha.match(/^(\d{4})-(\d{2})-(\d{2})$/);
                if (m) {
                    const anio = m[1];
                    const mes = m[2];
                    const dia = m[3];
                    return `${dia}-${mes}-${anio}`;
                }
            }

            const date = new Date(fecha);
            const dia = date.getUTCDate().toString().padStart(2, '0');
            const mes = (date.getUTCMonth() + 1).toString().padStart(2, '0');
            const anio = date.getUTCFullYear();
            return `${dia}-${mes}-${anio}`;
        } catch (error) {
            return 'Fecha inválida';
        }
    }

    getEstadoColor(estado: string | undefined | null): string {
        if (!estado || estado.trim() === '') {
            return 'estado-primario';
        }
        const estadoLower = estado.toLowerCase().trim();
        switch (estadoLower) {
            case 'liquidado':
                return 'estado-azul';
            case 'pendiente':
            case 'en proceso':
                return 'estado-naranja';
            case 'pagado':
                return 'estado-verde';
            case 'rechazado':
            case 'cancelado':
            case 'anulado':
                return 'estado-rojo';
            case 'cerrado':
                return 'estado-gris';
            default:
                return 'estado-primario';
        }
    }

    // Navegación
    volverALista(): void {
        // Volver al listado manteniendo el estado (filtros, paginación, etc.)
        this.navigationService.backToAgenciaList();
    }
}
